import React from "react";
import "./DatePicker.scss";
import { StorylineState } from "../../../store/storyline/types";
import { connect } from "react-redux";
import { RootState } from "../../../store";
import { updateParameterValue } from "../../../store/storyline/actions";
import { default as BaseDatePicker, type DatePickerProps } from "../../../shared/components/DatePicker";
import { PickerChangeHandlerContext, DateValidationError } from "@mui/x-date-pickers-pro";
import { DocumentedComponent } from "../../../shared/components/DocumentedComponent";
import { toDateFnsFormat, DateOnly } from "../../../shared/utilities";
import _ from "lodash";

// MUI's day-grid math (AdapterDateFns.getWeekArray) relies on date-fns comparisons that read
// valueOf(). DateOnly.valueOf() intentionally truncates to midnight UTC, which collapses the
// adapter's end-of-week boundary and drops the last grid cell (e.g. Jan 31 2026).
// 
// To prevent this from happening, we never hand a DateOnly value to the component.  Instead, we
// normalise the input (DateOnly parses strings/dates, ignoring any time/zone) and rebuild it as 
// a plain local Date (at midnight) of the same calendar day. Using the UTC components keeps the
// displayed day correct in every timezone, and handleChange converts the local Date back to a 
// DateOnly for persistence to the parameter specified.
const toLocalDate = (value?: string | DateOnly | null): Date | undefined => {
    if (!value) return undefined;
    const d = value instanceof DateOnly ? value : new DateOnly(value);
    return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
};

type Props = DatePickerProps & {
    storyline: StorylineState;
    updateParameterValue: typeof updateParameterValue;
    name: string;
    format?: string;
    minDate?: string | DateOnly;
    maxDate?: string | DateOnly;
    onChange?: (value: DateOnly, originalValue: string | DateOnly) => void | DateOnly | false;
    referenceDate?: string | DateOnly;
}

function _DatePicker(props: Props) {
    const { storyline, updateParameterValue, name, format, onChange, minDate: _minDate, maxDate: _maxDate, referenceDate: _referenceDate, ...other } = props;
    const [value, setValue] = React.useState(storyline.parameterValues.get(name) as DateOnly ?? null);
    const valueRef = React.useRef(value);
    const [open, setOpen] = React.useState(false);
    const [originalValue, setOriginalValue] = React.useState(value);
    const parameterValue = name ? storyline.parameterValues?.get(name) as DateOnly : undefined;

    const persistSelectedValue = (newValue: DateOnly, originalValue: DateOnly) => {
        const valueToPersist = onChange?.(newValue, originalValue) ?? newValue;

        // User explicitly cancelled the update, short-circuit here...
        if (valueToPersist === false) return;

        updateParameterValue(name, valueToPersist);
    };

    const handleChange = (newValue: Date | null, context: PickerChangeHandlerContext<DateValidationError>) => {
        if (context.validationError && context.validationError == "invalidDate") {
            // clear value
            setValue(null);
            valueRef.current = null;
            console.warn("User-selected date is invalid, ignoring change due to:", context.validationError, "Received value was:", newValue);
            return;
        }

        // Convert Date object to DateOnly for immediate/deferred persistence to the parameter...
        const dateOnly = newValue?.toDateOnly();
        setValue(dateOnly);
        valueRef.current = dateOnly; // The handleClose event handler will read this value in order to an update + execute in the same render cycle.

        if (!open) {
            persistSelectedValue(dateOnly, originalValue);
        }
    };

    const handleOpen = () => {
        setOpen(true);
        setOriginalValue(value);
    }

    const handleClose = () => {
        setOpen(false);

        if (!_.isEqual(originalValue, valueRef.current)) {
            persistSelectedValue(valueRef.current, originalValue);
        }
    };

    React.useEffect(() => {
        // match value state to parameter value
        if (Boolean(name) && parameterValue !== value) {
            setValue(parameterValue);
        }
    }, [parameterValue, name]);

    const minDate = React.useMemo(() => toLocalDate(_minDate), [_minDate]);
    const maxDate = React.useMemo(() => toLocalDate(_maxDate), [_maxDate]);
    const referenceDate = React.useMemo(() => toLocalDate(_referenceDate), [_referenceDate]);
    // MUI treats `value={undefined}` as an uncontrolled value, so we need to coalesce these to `null` instead...
    const displayValue = React.useMemo(() => toLocalDate(value) ?? null, [value]);

    return (
        <BaseDatePicker
            className="date-picker"
            {...other}
            open={open}
            onOpen={handleOpen}
            onClose={handleClose}
            minDate={minDate}
            maxDate={maxDate}
            referenceDate={referenceDate}
            value={displayValue}
            onChange={handleChange}
            format={format ? toDateFnsFormat(format) : "yyyy/MM/dd"}
        />
    );
}

const DatePicker = connect(
    (state: RootState) => ({
        storyline: state.storyline
    }),
    { updateParameterValue: updateParameterValue as any })(_DatePicker);

(DatePicker as DocumentedComponent).metadata = {
    description: `The \`DatePicker\` component allows the user to select a date value for a storyline parameter.  It does this via a calendar control inside a pop-up.

Note that this control does not allow for the selection of the time.  The selected date will be a Date object with the time set to local midnight.
    
_Use the \`DateTimePicker\` component in order to support the selection of the date + time._`,
    isSelfClosing: true,
    attributes: [
        { name: "name", type: "string", description: "The name of the parameter that the selected value will be read from and persisted to. The underlying component's `data-testid` attribute will be set to this value. This is useful for targeting the element via Playwright, Puppeteer or similar UI testing tools. Can be overridden via an explicit `data-testid` value." },
        { name: "format", type: "string", description: "The format of the date to be displayed in the input field.  This is a string that is passed to the date-fns library. See https://date-fns.org/v4.1.0/docs/format for more details" },
        { name: "minDate", type: "object", exactType: "string | Date", description: "The minimum date that can be selected.  This can be a string or a Date object" },
        { name: "maxDate", type: "object", exactType: "string | Date", description: "The maximum date that can be selected.  This can be a string or a Date object" },
        { name: "onChange", type: "function", exactType: "(newValue: Date, originalValue: string | Date) => void | DateOnly | false", template: "onChange={(newValue, _originalValue) => {$1}}", description: "An optional callback function that will be invoked when the selected date changes.  The updated value and the original value are passed in as arguments.  If this function returns an explicit `false` value, the new value will not be persisted to the parameter.  Any other return value will be used as a substitute for the user-selected value and will be persisted to the parameter instead.  This is useful in cases where a derivative of the selected value is desired (such as the first/last day of the selected month, next working day, etc.)." },
        { name: "referenceDate", type: "object", exactType: "string | Date", description: "An optional date that is used as the reference point for the calendar display when no date has been selected yet. This will render the calendar view in the context of this date." }
    ]
};

export default DatePicker;